import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import useDailyChecks from "../../data/useDailyChecks";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ICreateUpdateDailyCheckDefinitionRequest, IDailyCheckDefinition } from "../../models/DailyChecks";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import IconSelectInput, { IconOption } from "../shared/Inputs/IconSelectInput";
import SwitchInput from "../shared/Inputs/SwitchInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { dailyCheckFallbackIcon, dailyCheckIcons, findDailyCheckIcon } from "./dailyCheckIcons";

type FormValues = {
  name: string,
  icon: string,
  order: number,
  isActive: boolean,
};

interface DailyCheckDefinitionModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  definition?: IDailyCheckDefinition;
  defaultOrder: number;
}

export default function DailyCheckDefinitionModal({ open, setOpen, definition, defaultOrder }: DailyCheckDefinitionModalProps) {
  const dailyCheckLabel = "Daily Check";
  const { saveHandlers } = useDefaultCRUDHandlers(dailyCheckLabel);
  const { deleteHandlers } = useDefaultCRUDHandlers(dailyCheckLabel);
  const { createDefinition, updateDefinition, deleteDefinition } = useDailyChecks();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();

  // An icon the Yard app doesn't recognise still shows as a checklist there, so keep it
  // selectable rather than silently dropping it when the definition is saved.
  const iconOptions: IconOption[] = definition?.icon && !findDailyCheckIcon(definition.icon)
    ? [{ id: definition.icon, name: `${definition.icon} (not in the Yard app)` }, ...dailyCheckIcons]
    : dailyCheckIcons;

  const onSubmit = (data: FormValues) => {
    const updatedDefinition: ICreateUpdateDailyCheckDefinitionRequest = {
      ...definition,
      name: data.name,
      icon: data.icon?.trim() || null,
      order: Number(data.order),
      isActive: data.isActive,
    };

    if (definition !== undefined) {
      updateDefinition.mutate(updatedDefinition, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      createDefinition.mutate(updatedDefinition, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  };

  const handleDelete = () => {
    deleteDefinition.mutate(definition?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  useEffect(() => {
    if (definition !== undefined) {
      reset({
        name: definition.name,
        icon: definition.icon ?? "",
        order: definition.order,
        isActive: definition.isActive,
      });
    } else {
      reset({
        name: "",
        icon: "",
        order: defaultOrder,
        isActive: true,
      });
    }
  }, [definition, open, reset, defaultOrder]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={`Delete ${dailyCheckLabel}`} message={`Are you sure you want to delete this ${dailyCheckLabel.toLowerCase()} and all of its steps? This action cannot be undone.`}
        confirmButtonText="Delete" onConfirm={handleDelete} isLoading={deleteDefinition.isLoading}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <h1 className="text-xl">{`${definition ? "Edit" : "Add"} a ${dailyCheckLabel}`}</h1>
        <h1 className="text-sm text-gray-500">{`Each ${dailyCheckLabel.toLowerCase()} has its own set of steps to be ticked off every day.`}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label="Name" registerName="name" register={register} required={true} />
              <IconSelectInput
                key={`${definition?.id ?? "new"}-${open}`}
                label="Icon"
                registerName="icon"
                register={register}
                setValue={setValue}
                watch={watch}
                options={iconOptions}
                clearOption={dailyCheckFallbackIcon}
                helpText="Shown next to the daily check in the Yard app. With no icon selected it shows a checklist."
              />
              <input type="hidden" {...register("order")} />

              <SwitchInput label="Is Active" registerName="isActive" register={register} setValue={setValue} watch={watch} />

              <ButtonInput
                label={`${definition ? "Save Changes" : "Create"}`}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
                isLoading={createDefinition.isLoading || updateDefinition.isLoading}
              />
              {definition !== undefined && (
                <ButtonInput label="Delete" isSubmit={false} onClick={() => (setDeleteOpen(true))} classes="!bg-red-500 hover:!bg-red-600" />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
